import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, finalize, map, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { App } from '../../models/app.model';
import { AppSearchService } from '../../services/app-search.service';

@Component({
    selector: 'app-selector-control',
    templateUrl: 'app-selector-control.component.html',
    styleUrls: ['app-selector-control.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AppSelectorControlComponent implements OnInit, OnDestroy {
    @Input() placeholderText: string;
    @Input() parentFormGroup: FormGroup;
    @Input() controlName: string;
    @Input() selectedAppId: string;

    appFilterControl: FormControl<string> = new FormControl<string>('');
    filteredApps: Subject<App[]> = new Subject<App[]>();
    readonly searching = signal(false);

    private readonly destroy$ = new Subject<void>();
    private searchResults: App[] = [];
    private selectedApp?: App;

    constructor(private readonly appSearchService: AppSearchService) { }

    ngOnInit(): void {
        const selectedControl = this.parentFormGroup.get(this.controlName);
        if (this.selectedAppId && !selectedControl?.value) {
            selectedControl?.setValue(this.selectedAppId, { emitEvent: false });
        }

        selectedControl?.valueChanges.pipe(
            startWith(selectedControl.value as string | null),
            distinctUntilChanged(),
            switchMap((selectedId: string | null) => {
                if (!selectedId) {
                    this.selectedApp = undefined;
                    if (this.searchResults.length) {
                        this.emitAvailableApps();
                    }
                    return EMPTY;
                }

                const knownApp = this.searchResults.find(app => app.id === selectedId)
                    ?? (this.selectedApp?.id === selectedId ? this.selectedApp : undefined);
                if (knownApp) {
                    this.selectedApp = knownApp;
                    this.emitAvailableApps();
                    return EMPTY;
                }

                return this.appSearchService.getAppById(selectedId).pipe(
                    catchError(() => of([])),
                    map(result => ({ selectedId, result }))
                );
            }),
            takeUntil(this.destroy$)
        ).subscribe(({ selectedId, result }) => {
            this.selectedApp = this.searchResults.find(app => app.id === selectedId)
                ?? result.find(app => app.id === selectedId);
            this.emitAvailableApps();
        });

        this.appFilterControl.valueChanges
            .pipe(
                map(search => search?.trim() ?? ''),
                debounceTime(500),
                distinctUntilChanged(),
                switchMap(search => {
                    if (!search) {
                        this.searching.set(false);
                        return EMPTY;
                    }

                    this.searching.set(true);
                    return this.appSearchService.getApps(search).pipe(
                        catchError(() => of([])),
                        finalize(() => this.searching.set(false))
                    );
                }),
                takeUntil(this.destroy$)
            ).subscribe(result => {
                this.searchResults = this.uniqueApps(result);
                const selectedId = selectedControl?.value as string | null;
                this.selectedApp = this.searchResults.find(app => app.id === selectedId) ?? this.selectedApp;
                this.emitAvailableApps();
            });
    }

    private emitAvailableApps(): void {
        const selectedId = this.parentFormGroup.get(this.controlName)?.value as string | null;
        const selected = this.selectedApp?.id === selectedId ? this.selectedApp : undefined;
        const results = selected
            ? [selected, ...this.searchResults.filter(app => app.id !== selected.id)]
            : this.searchResults;
        this.filteredApps.next(results);
    }

    private uniqueApps(apps: App[]): App[] {
        const ids = new Set<string>();
        return apps.filter(app => {
            if (ids.has(app.id)) {
                return false;
            }

            ids.add(app.id);
            return true;
        });
    }


    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
